'use client'
import Link from 'next/link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Award, ChevronRight, Trophy, Sparkles, Eye, Flame, ShieldCheck, Crown, Lock } from 'lucide-react'
import { AchievementBadge } from './medallions/achievement-badge'

export interface AchievementItem {
  id: string
  name: string
  desc: string
  state: 'unlocked' | 'progress' | 'locked'
  color: string
  iconKey: 'trophy' | 'sparkles' | 'eye' | 'flame' | 'shield' | 'crown' | 'flag'
  progress?: number
  total?: number
  unlockedAgo?: string
}

export interface AchievementsRowProps {
  items: AchievementItem[]
  seeAllHref?: string
}

export function AchievementsRow({ items, seeAllHref = '#' }: AchievementsRowProps) {
  const iconMap: Record<AchievementItem['iconKey'], React.ReactNode> = {
    trophy: <Trophy className="h-5 w-5" />,
    flag: <Trophy className="h-5 w-5" />,
    sparkles: <Sparkles className="h-5 w-5" />,
    eye: <Eye className="h-5 w-5" />,
    flame: <Flame className="h-5 w-5" />,
    shield: <ShieldCheck className="h-5 w-5" />,
    crown: <Crown className="h-5 w-5" />,
  }
  const unlockedN = items.filter((a) => a.state === 'unlocked').length
  const inProgressN = items.filter((a) => a.state === 'progress').length
  const lockedN = items.filter((a) => a.state === 'locked').length

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-2 pb-3">
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5 text-amber-500" /> Achievements
          <Badge variant="outline" className="ml-1 text-[10px]">
            {unlockedN} unlocked · {inProgressN} in progress · {lockedN} locked
          </Badge>
        </CardTitle>
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
          <Link href={seeAllHref}>See all <ChevronRight className="h-4 w-4" /></Link>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {items.map((a) => (
            <div key={a.id} className={
              'relative flex flex-col items-center text-center rounded-xl border p-3 transition-colors ' +
              (a.state === 'unlocked' ? 'border-border bg-muted/40'
                : a.state === 'progress' ? 'border-border bg-background'
                : 'border-dashed border-border bg-transparent')
            }>
              {a.state === 'locked' && <Lock className="absolute top-2 right-2 h-3 w-3 text-muted-foreground" />}
              <AchievementBadge color={a.color} icon={iconMap[a.iconKey]} size={52} state={a.state} />
              <div className="mt-2 text-xs font-semibold text-foreground line-clamp-1">{a.name}</div>
              <div className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5">{a.desc}</div>
              {a.state === 'progress' && a.progress != null && a.total != null && (
                <div className="mt-2 w-full">
                  <Progress value={(a.progress / a.total) * 100} className="h-1" />
                  <div className="mt-1 text-[10px] tabular-nums text-muted-foreground">{a.progress}/{a.total}</div>
                </div>
              )}
              {a.state === 'unlocked' && a.unlockedAgo && (
                <div className="mt-2 text-[10px] text-emerald-600 dark:text-emerald-400">Unlocked {a.unlockedAgo} ago</div>
              )}
              {a.state === 'unlocked' && !a.unlockedAgo && (
                <div className="mt-2 text-[10px] text-emerald-600 dark:text-emerald-400">Unlocked</div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
